import React, { Component } from "react";
import { SectionAction } from "@eres/forms-web/FormSubSection";
import Strings from "@my-dld/assets/strings";
import _ from "lodash";
import { WebFormFieldProps } from "@eres/forms-web";
import StateHolder from "@eres/forms/StateHolder";
import { defaultErrorManager } from "@eres/forms/errormanager";
import { ErrorContainer } from "@eres/forms";
import { JQModal } from "@my-dld/components/Popup/Utils";

interface ContractItemsProps<T> {
	items?: T[];
	onItemsChanged: ( items: T[] ) => void;
	renderItem: ( props: ItemViewProps<T>, state: T, fieldProps: Partial<WebFormFieldProps<any, any>> ) => React.ReactElement;
	buildItemForAddNew: () => T;
	dialogId: string;
	extractUpdatedData: ( props: NewItemProps<T>, state: NewItemState<T> ) => T;
	validateItem: ( prevResult: boolean, data: T, errorContainer: ErrorContainer, props: NewItemProps<T>, state: NewItemState<T> ) => boolean;
	renderForm: ( props: NewItemProps<T>, state: NewItemState<T>, fieldProps: Partial<WebFormFieldProps<any, any>>, onDone: () => void ) => React.ReactElement;
}

interface ContractItemsState<T> {
	isEdit: boolean;
	item?: T;
}

export default class ContractItems<T> extends Component<ContractItemsProps<T>, ContractItemsState<T>> {
	addForm = React.createRef<NewItem<T>>();

	constructor( props: ContractItemsProps<T> ) {
		super( props );
		this.state = {
			isEdit: false
		};
	}

	onAddNewItem = () => {
		this.setState( {
			isEdit: false,
		}, () => {
			this.addForm.current?.show( this.props.buildItemForAddNew() );
		} );
	};

	render() {
		const { items, dialogId } = this.props;
		const { item, isEdit } = this.state;
		return <>
			{ items && items.map( ( value, index ) => {
				return <ItemView<T>
					position={ index + 1 }
					key={ index + "" }
					data={ value }
					renderItem={ this.props.renderItem }
				/>;
			} ) }
			<NewItem
				renderForm={ this.props.renderForm }
				extractUpdatedData={ this.props.extractUpdatedData }
				validateItem={ this.props.validateItem }
				dialogId={ dialogId }
				ref={ this.addForm }
				value={ item }
				isEdit={ isEdit }
				onDone={ this.onNewItem }
			/>
		</>;
	}

	onNewItem = ( value: T, isEdit: boolean, orgValue?: T ) => {
		if ( !isEdit ) {
			const ov = this.props.items || [];
			const ns = [
				...ov,
				value,
			];
			this.props.onItemsChanged( ns );
		} else {
			const np = this.props.items?.map( x => x === orgValue ? value : x );
			this.props.onItemsChanged( np || [] );
		}
	};
	onEditItem = ( data: T ) => {
		this.setState( {
			isEdit: true,
			item: data
		}, () => {
			this.addForm.current?.show( this.state.item );
		} );
	};

}

export interface ItemViewProps<T> {
	data?: T;
	position: number;
	renderItem: ( props: ItemViewProps<T>, state: T, fieldProps: Partial<WebFormFieldProps<any, any>> ) => React.ReactElement;
}

export const EditAction: SectionAction = {
	id: "edit",
	icon: "far fa-edit",
	accLabel: Strings.edit
};
export const RemoveAction: SectionAction = {
	id: "delete",
	icon: "far fa-trash-alt",
	accLabel: Strings.delete,
};

export class ItemView<T> extends Component<ItemViewProps<T>, T> {

	constructor( props: ItemViewProps<T> ) {
		super( props );
		this.state = {
			...props.data
		} as T;
	}

	componentDidUpdate( prevProps: Readonly<ItemViewProps<T>>, prevState: Readonly<T>, snapshot?: any ) {
		if ( !_.isEqual( prevProps.data, this.props.data ) ) {
			this.setState( {
				...this.props.data
			} );
		}
	}

	render() {
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-4 form-group",
			errorManager: defaultErrorManager,
		};
		return this.props.renderItem( this.props, this.state, filedProps );
	}
}


export interface NewItemProps<T> {
	dialogId: string;
	isEdit: boolean;
	value?: T;
	onDone: ( value: T, isEdit: boolean, orgValue?: T ) => void;
	extractUpdatedData: ( props: NewItemProps<T>, state: NewItemState<T> ) => T;
	validateItem: ( prevResult: boolean, data: T, errorContainer: ErrorContainer, props: NewItemProps<T>, state: NewItemState<T> ) => boolean;
	renderForm: ( props: NewItemProps<T>, state: NewItemState<T>, fieldProps: Partial<WebFormFieldProps<any, any>>, onDone: () => void ) => React.ReactElement;
}

export type NewItemState<T> = {
	isValid: boolean;
	errorContainer: ErrorContainer;
} & T

class NewItem<T> extends Component<NewItemProps<T>, NewItemState<T>> {
	constructor( props: NewItemProps<T> ) {
		super( props );
		this.state = {
			...props.value,
			isValid: false,
			errorContainer: {},
		} as NewItemState<T>;
	}

	show = ( value?: T ) => {
		if ( value ) {
			let state: Partial<NewItemState<T>> = { ...value };
			this.setState( state as NewItemState<T> );
		}
		JQModal( this.props.dialogId ).show();
	};

	hide = () => {
		JQModal( this.props.dialogId ).hide();
	};
	getUpdatedData = ( state: NewItemState<T> = this.state ) => {
		return this.props.extractUpdatedData( this.props, state );
	};
	isValid = () => {
		let isValid = true;
		const errorContainer: ErrorContainer = {};
		const nd = this.getUpdatedData();
		isValid = this.props.validateItem( isValid, nd, errorContainer, this.props, this.state );
		this.setState( {
			errorContainer: errorContainer,
		} as NewItemState<T> );
		return isValid;
	};
	updateValidity = () => {
		this.setState( {
			isValid: this.isValid(),
		} as NewItemState<T> );
	};

	componentDidUpdate( prevProps: Readonly<NewItemProps<T>>, prevState: Readonly<NewItemState<T>>, snapshot?: any ) {
		const od = this.getUpdatedData( prevState );
		const nd = this.getUpdatedData( this.state );
		if ( !_.isEqual( od, nd ) ) {
			this.updateValidity();
		}
		if ( !_.isEqual( prevProps.value, this.props.value ) ) {
			this.setState( {
				...this.props.value
			} );
		}
	}

	render() {
		const filedProps: Partial<WebFormFieldProps<any, any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-9 form-group",
			errorManager: defaultErrorManager,
		};
		return this.props.renderForm( this.props, this.state, filedProps, this.onAdd );
	}

	onAdd = () => {
		const d = this.getUpdatedData();
		this.hide();
		let edit = this.props.isEdit;
		this.props.onDone( d as T, edit, edit ? this.props.value : undefined );
	};
}
